import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Button,
  Divider,
  Skeleton,
  Collapse,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import RefreshIcon from "@mui/icons-material/Refresh";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ScheduleIcon from "@mui/icons-material/Schedule";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import NotificationsOffIcon from "@mui/icons-material/NotificationsOff";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import HistoryIcon from "@mui/icons-material/History";
import PeopleIcon from "@mui/icons-material/People";

import { getScanStatus, getWebhookSubscriptions, getScanLogs } from "../../services/apiService";
import { formatDate, timeAgo } from "./shared/formatters";

const POPPINS = { fontFamily: "'Poppins', sans-serif" };
const HEADER_COLOR = "#1B2559";
const PRIMARY_COLOR = "#1976d2";

const StatCard = ({ title, value, sub, icon, color = PRIMARY_COLOR }) => (
  <Paper
    elevation={1}
    sx={{
      p: 2.5,
      borderRadius: 2,
      display: "flex",
      alignItems: "flex-start",
      gap: 2,
    }}
  >
    <Box
      sx={{
        width: 44,
        height: 44,
        borderRadius: 2,
        backgroundColor: `${color}18`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        color,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={POPPINS}>
        {title}
      </Typography>
      <Typography
        variant="body1"
        sx={{ ...POPPINS, fontWeight: 700, color: HEADER_COLOR, wordBreak: "break-all" }}
      >
        {value ?? "—"}
      </Typography>
      {sub && (
        <Typography variant="caption" color="text.secondary" sx={POPPINS}>
          {sub}
        </Typography>
      )}
    </Box>
  </Paper>
);

// ── Per-user breakdown row (expandable) ──────────────────────────────
const PerUserRow = ({ row }) => {
  const [open, setOpen] = useState(false);
  let perUser = [];
  try {
    perUser = row.per_user_json ? JSON.parse(row.per_user_json) : [];
  } catch {
    perUser = [];
  }

  const statusColor = row.status === "ok" ? "success" : "error";

  return (
    <>
      <TableRow hover sx={{ "& > *": { borderBottom: "unset" } }}>
        <TableCell sx={{ width: 36, p: 0.5 }}>
          {perUser.length > 0 && (
            <IconButton size="small" onClick={() => setOpen((p) => !p)}>
              {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
          )}
        </TableCell>
        <TableCell>
          <Typography variant="body2" sx={{ ...POPPINS, whiteSpace: "nowrap" }}>
            {row.started_at ? formatDate(row.started_at) : "—"}
          </Typography>
        </TableCell>
        <TableCell>
          <Chip
            label={row.status}
            size="small"
            color={statusColor}
            variant="outlined"
            sx={{ ...POPPINS, fontSize: "0.68rem" }}
          />
        </TableCell>
        <TableCell align="right">
          <Typography variant="body2" sx={POPPINS}>{row.users_total ?? "—"}</Typography>
        </TableCell>
        <TableCell align="right">
          <Typography variant="body2" sx={{ ...POPPINS, color: "#4caf50", fontWeight: 600 }}>
            {row.files_found ?? 0}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Typography variant="body2" sx={{ ...POPPINS, color: PRIMARY_COLOR, fontWeight: 600 }}>
            {row.files_saved ?? 0}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Typography variant="body2" sx={{ ...POPPINS, color: "#7b61ff", fontWeight: 600 }}>
            {row.jobs_triggered ?? 0}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2" sx={{ ...POPPINS, color: "#888" }}>
            {row.completed_at ? timeAgo(row.started_at) + " ago" : "—"}
          </Typography>
        </TableCell>
      </TableRow>

      {/* Per-user breakdown */}
      {perUser.length > 0 && (
        <TableRow>
          <TableCell colSpan={8} sx={{ py: 0, backgroundColor: "#f9fafb" }}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ m: 1.5 }}>
                <Typography
                  variant="caption"
                  sx={{ ...POPPINS, fontWeight: 700, color: HEADER_COLOR, display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}
                >
                  <PeopleIcon sx={{ fontSize: 14 }} /> Per-User Breakdown
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#eef0f8" }}>
                      {["User", "Folder", "Status", "Found", "Saved", "Jobs"].map((h) => (
                        <TableCell key={h} sx={{ ...POPPINS, fontSize: "0.72rem", fontWeight: 700, color: HEADER_COLOR }}>
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {perUser.map((u, i) => (
                      <TableRow key={i}>
                        <TableCell>
                          <Tooltip title={u.email_id || ""}>
                            <Typography variant="caption" sx={{ ...POPPINS, maxWidth: 160, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {u.email_id || u.user_id || "—"}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" sx={{ ...POPPINS, color: "#666" }}>{u.folder || "—"}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={u.status === "skipped_no_folder" ? "Skipped" : u.status}
                            size="small"
                            color={u.status === "ok" ? "success" : u.status === "skipped_no_folder" ? "warning" : "error"}
                            variant="outlined"
                            sx={{ ...POPPINS, fontSize: "0.65rem" }}
                          />
                        </TableCell>
                        <TableCell align="right"><Typography variant="caption" sx={POPPINS}>{u.files_found ?? 0}</Typography></TableCell>
                        <TableCell align="right"><Typography variant="caption" sx={POPPINS}>{u.files_saved ?? 0}</Typography></TableCell>
                        <TableCell align="right"><Typography variant="caption" sx={POPPINS}>{u.jobs_triggered ?? 0}</Typography></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

/**
 * SyncStatusDashboard — shows SharePoint scanner schedule info,
 * processing history (scan logs), and webhook subscription table.
 */
const SyncStatusDashboard = () => {
  const [scanStatus, setScanStatus] = useState(null);
  const [webhooks, setWebhooks] = useState([]);
  const [loadingScan, setLoadingScan] = useState(true);
  const [loadingWebhooks, setLoadingWebhooks] = useState(true);
  const [error, setError] = useState(null);
  const [triggering, setTriggering] = useState(false);
  const [triggerMsg, setTriggerMsg] = useState(null);

  // Processing history
  const [logs, setLogs] = useState([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsPage, setLogsPage] = useState(0);
  const [logsRowsPerPage, setLogsRowsPerPage] = useState(10);
  const [loadingLogs, setLoadingLogs] = useState(true);

  const fetchLogs = useCallback(async (page, rowsPerPage) => {
    setLoadingLogs(true);
    try {
      const res = await getScanLogs({ limit: rowsPerPage, offset: page * rowsPerPage });
      if (res?.data) {
        setLogs(res.data);
        setLogsTotal(res.total ?? 0);
      }
    } catch {
      // non-fatal — logs section will show empty state
    } finally {
      setLoadingLogs(false);
    }
  }, []);

  const fetchAll = useCallback(async (silent = false) => {
    if (!silent) {
      setLoadingScan(true);
      setLoadingWebhooks(true);
    }
    setError(null);

    const [scanRes, webhookRes] = await Promise.allSettled([
      getScanStatus(),
      getWebhookSubscriptions(),
    ]);

    if (scanRes.status === "fulfilled") {
      const d = scanRes.value;
      if (!d?.error) setScanStatus(d);
      else setError(d.message || "Failed to load scan status.");
    } else {
      setError("Failed to load scan status.");
    }
    setLoadingScan(false);

    if (webhookRes.status === "fulfilled") {
      const d = webhookRes.value;
      setWebhooks(d?.data ?? []);
    }
    setLoadingWebhooks(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => { fetchLogs(logsPage, logsRowsPerPage); }, [fetchLogs, logsPage, logsRowsPerPage]);

  const handleTriggerScan = async () => {
    setTriggering(true);
    setTriggerMsg(null);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_FLASK_API_ENDPOINT || ""}/sharepoint/scan/trigger`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("accessToken") || ""}`,
          },
        }
      );
      if (res.ok) {
        setTriggerMsg("Scan triggered successfully. Results will update shortly.");
        setTimeout(() => { fetchAll(true); fetchLogs(0, logsRowsPerPage); }, 5000);
      } else {
        setTriggerMsg("Failed to trigger scan. Please try again.");
      }
    } catch {
      setTriggerMsg("Network error while triggering scan.");
    } finally {
      setTriggering(false);
    }
  };

  const isSubscriptionExpired = (expiry) => {
    if (!expiry) return false;
    return new Date(expiry) < new Date();
  };

  const lastResult = scanStatus?.last_result ?? {};
  const totalAdded = Array.isArray(lastResult.users)
    ? lastResult.users.reduce((acc, u) => acc + (u.new_files ?? 0), 0)
    : null;
  const totalDeleted = Array.isArray(lastResult.users)
    ? lastResult.users.reduce((acc, u) => acc + (u.reconciled_deleted ?? 0), 0)
    : null;

  return (
    <Box sx={{ p: 2 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2.5,
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Typography variant="h6" sx={{ ...POPPINS, fontWeight: 700, color: HEADER_COLOR }}>
          Sync &amp; Scan Status
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={triggering ? <CircularProgress size={14} color="inherit" /> : <PlayArrowIcon />}
            disabled={triggering}
            onClick={handleTriggerScan}
            sx={{ ...POPPINS, textTransform: "none", borderRadius: 2 }}
          >
            Trigger Scan Now
          </Button>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={() => { fetchAll(true); fetchLogs(logsPage, logsRowsPerPage); }} sx={{ color: PRIMARY_COLOR }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {triggerMsg && (
        <Alert
          severity={triggerMsg.startsWith("Scan triggered") ? "success" : "warning"}
          sx={{ mb: 2, ...POPPINS }}
          onClose={() => setTriggerMsg(null)}
        >
          {triggerMsg}
        </Alert>
      )}

      {error && <Alert severity="error" sx={{ mb: 2, ...POPPINS }}>{error}</Alert>}

      {/* Stat cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          {loadingScan ? (
            <Skeleton variant="rectangular" height={90} sx={{ borderRadius: 2 }} />
          ) : (
            <StatCard
              title="Scanner"
              value={scanStatus?.scan_enabled ? "Enabled" : "Disabled"}
              sub={`Daily at ${scanStatus?.cron_time ?? "—"} UTC`}
              icon={
                scanStatus?.scan_enabled ? (
                  <CheckCircleIcon />
                ) : (
                  <CancelIcon />
                )
              }
              color={scanStatus?.scan_enabled ? "#4caf50" : "#f44336"}
            />
          )}
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          {loadingScan ? (
            <Skeleton variant="rectangular" height={90} sx={{ borderRadius: 2 }} />
          ) : (
            <StatCard
              title="Last Scan"
              value={scanStatus?.last_scan_at ? timeAgo(scanStatus.last_scan_at) : "Never"}
              sub={scanStatus?.last_scan_at ? formatDate(scanStatus.last_scan_at) : "No scans run yet"}
              icon={<ScheduleIcon />}
            />
          )}
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          {loadingScan ? (
            <Skeleton variant="rectangular" height={90} sx={{ borderRadius: 2 }} />
          ) : (
            <StatCard
              title="Next Run"
              value={scanStatus?.next_run_at ? timeAgo(scanStatus.next_run_at) : "—"}
              sub={scanStatus?.next_run_at ? formatDate(scanStatus.next_run_at) : "Scheduler not running"}
              icon={<ScheduleIcon />}
              color="#ff9800"
            />
          )}
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          {loadingScan ? (
            <Skeleton variant="rectangular" height={90} sx={{ borderRadius: 2 }} />
          ) : (
            <StatCard
              title="Last Run Result"
              value={
                totalAdded !== null
                  ? `+${totalAdded} added / ${totalDeleted ?? 0} removed`
                  : "No data"
              }
              sub={lastResult.checked_at ? formatDate(lastResult.checked_at) : ""}
              icon={<PlayArrowIcon />}
              color="#7b61ff"
            />
          )}
        </Grid>
      </Grid>

      <Divider sx={{ mb: 2.5 }} />

      {/* ── Processing History ──────────────────────────────────────── */}
      <Typography
        variant="subtitle2"
        sx={{ ...POPPINS, fontWeight: 700, color: HEADER_COLOR, mb: 1.5, display: "flex", alignItems: "center", gap: 0.75 }}
      >
        <HistoryIcon sx={{ fontSize: 18 }} /> Processing History
      </Typography>

      {loadingLogs ? (
        <Skeleton variant="rectangular" height={180} sx={{ borderRadius: 2, mb: 3 }} />
      ) : logs.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 4, mb: 3 }}>
          <HistoryIcon sx={{ fontSize: 40, color: "#c8c8c8", mb: 1 }} />
          <Typography variant="body2" color="text.secondary" sx={POPPINS}>
            No scan runs recorded yet.
          </Typography>
        </Box>
      ) : (
        <Paper elevation={1} sx={{ borderRadius: 2, mb: 3 }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f5f7fb" }}>
                  <TableCell sx={{ width: 36 }} />
                  {["Started At", "Status", "Users", "Files Found", "Files Saved", "Jobs Triggered", "Duration"].map((h) => (
                    <TableCell
                      key={h}
                      align={["Users", "Files Found", "Files Saved", "Jobs Triggered"].includes(h) ? "right" : "left"}
                      sx={{ ...POPPINS, fontWeight: 700, fontSize: "0.78rem", color: HEADER_COLOR }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.map((row) => (
                  <PerUserRow key={row.log_id} row={row} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={logsTotal}
            page={logsPage}
            onPageChange={(_, p) => setLogsPage(p)}
            rowsPerPage={logsRowsPerPage}
            onRowsPerPageChange={(e) => { setLogsRowsPerPage(parseInt(e.target.value, 10)); setLogsPage(0); }}
            rowsPerPageOptions={[5, 10, 20]}
            sx={{ ...POPPINS, fontSize: "0.8rem", borderTop: "1px solid #e0e0e0" }}
          />
        </Paper>
      )}

      <Divider sx={{ mb: 2.5 }} />

      {/* ── Webhook Subscriptions ────────────────────────────────────── */}
      <Typography
        variant="subtitle2"
        sx={{ ...POPPINS, fontWeight: 700, color: HEADER_COLOR, mb: 1.5 }}
      >
        Webhook Subscriptions
      </Typography>

      {loadingWebhooks ? (
        <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
      ) : webhooks.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 5 }}>
          <NotificationsOffIcon sx={{ fontSize: 40, color: "#c8c8c8", mb: 1 }} />
          <Typography variant="body2" color="text.secondary" sx={POPPINS}>
            No webhook subscriptions registered.
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} elevation={1} sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f5f7fb" }}>
                {["Subscription ID", "Resource", "Change Types", "Expires", "Status"].map((h) => (
                  <TableCell
                    key={h}
                    sx={{ ...POPPINS, fontWeight: 700, fontSize: "0.78rem", color: HEADER_COLOR }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {webhooks.map((w) => {
                const expired = isSubscriptionExpired(w.expiry_datetime);
                const isActive = w.is_active && !expired;
                return (
                  <TableRow key={w.subscription_id} hover>
                    <TableCell>
                      <Tooltip title={w.subscription_id}>
                        <Typography
                          variant="body2"
                          sx={{
                            ...POPPINS,
                            fontFamily: "monospace",
                            maxWidth: 160,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {w.subscription_id?.slice(0, 16)}…
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={w.resource}>
                        <Typography
                          variant="body2"
                          sx={{
                            ...POPPINS,
                            maxWidth: 200,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            color: "#555",
                          }}
                        >
                          {w.resource || "—"}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ ...POPPINS, color: "#555" }}>
                        {w.change_types || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ ...POPPINS, color: expired ? "#f44336" : "#555", whiteSpace: "nowrap" }}
                      >
                        {formatDate(w.expiry_datetime)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={
                          isActive ? (
                            <NotificationsActiveIcon sx={{ fontSize: "14px !important" }} />
                          ) : (
                            <NotificationsOffIcon sx={{ fontSize: "14px !important" }} />
                          )
                        }
                        label={isActive ? "Active" : expired ? "Expired" : "Inactive"}
                        size="small"
                        color={isActive ? "success" : "default"}
                        variant={isActive ? "filled" : "outlined"}
                        sx={{ ...POPPINS, fontSize: "0.68rem" }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default SyncStatusDashboard;
